// slices/lectures/lecturesSlice.ts
import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { Lecture } from '../../types/Lecture';
import moment from 'moment';

type LecturesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// Configure the adapter with a sortComparer function
const lecturesAdapter = createEntityAdapter<Lecture>({
  // Sort lectures by date first, then by createdAt in ascending order
  sortComparer: (a, b) => {
    const dateA = moment(a.date);
    const dateB = moment(b.date);

    // First compare dates
    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;

    // If dates are equal, compare createdAt
    const createdAtA = moment(a.createdAt);
    const createdAtB = moment(b.createdAt);
    return createdAtA.isBefore(createdAtB)
      ? -1
      : createdAtA.isAfter(createdAtB)
        ? 1
        : 0;
  },
});

interface LecturesState
  extends ReturnType<typeof lecturesAdapter.getInitialState> {
  status: LecturesStatus;
  error: string | null;
  // Optional: Index by examId if helpful
  // lecturesByExamId: Record<string, string[]>;
}

const initialState: LecturesState = lecturesAdapter.getInitialState({
  status: 'idle',
  error: null,
  // lecturesByExamId: {},
});

export const lecturesSlice = createSlice({
  name: 'lectures',
  initialState,
  reducers: {
    lecturesLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    lecturesReceived: (state, action: PayloadAction<Lecture[]>) => {
      lecturesAdapter.setAll(state, action.payload);
      state.status = 'succeeded';
    },
    lectureReceived: (state, action: PayloadAction<Lecture>) => {
      lecturesAdapter.upsertOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    lectureDeleted: (state, action: PayloadAction<string>) => {
      lecturesAdapter.removeOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    lecturesFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load lectures';
    },
    clearLectures: (state) => {
      lecturesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      // state.lecturesByExamId = {};
    },
    // Add more reducers (addOne, updateOne) as needed
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      lecturesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
      // state.lecturesByExamId = {};
    });
  },
});

export const {
  lecturesLoading,
  lecturesReceived,
  lectureReceived,
  lectureDeleted,
  lecturesFailed,
  clearLectures,
} = lecturesSlice.actions;

export const lecturesSelectors = lecturesAdapter.getSelectors(
  (state: { lectures: LecturesState }) => state.lectures
);

export default lecturesSlice.reducer;

// Import the logout action from authSlice
import { logout } from '../auth/authSlice';
